import React from 'react';
import { Building2, Rocket, Briefcase, Code2, ArrowRight } from 'lucide-react';

const USE_CASES = [
  {
    icon: Building2,
    title: 'Agences',
    description: 'Gérez plusieurs clients et projets simultanément. Suivez la rentabilité de chaque mission. Partagez l\'avancement avec vos clients.',
    benefits: ['Multi-projets', 'Suivi budget client', 'Portail client'],
    color: 'indigo'
  },
  {
    icon: Rocket,
    title: 'Startups',
    description: 'Avancez vite sans sacrifier la visibilité. Coordonnez produit, tech et business dans un seul espace.',
    benefits: ['Sprints agiles', 'Roadmap claire', 'Collaboration temps réel'],
    color: 'violet'
  },
  {
    icon: Briefcase,
    title: 'PME',
    description: 'Structurez vos projets internes. Suivez la charge de vos équipes. Gardez le contrôle sur les délais et budgets.',
    benefits: ['Vue direction', 'Rapports automatiques', 'Historique complet'],
    color: 'emerald'
  },
  {
    icon: Code2,
    title: 'Équipes Produit & IT',
    description: 'De la roadmap au déploiement. Gérez backlog, sprints et dépendances techniques avec précision.',
    benefits: ['Backlog priorisé', 'Dépendances tâches', 'Intégrations dev'],
    color: 'amber'
  }
];

const colorMap = {
  indigo: 'from-indigo-500 to-indigo-600',
  violet: 'from-violet-500 to-violet-600',
  emerald: 'from-emerald-500 to-emerald-600',
  amber: 'from-amber-500 to-amber-600'
};

export function UseCasesSection() {
  return (
    <section id="use-cases" className="py-24 md:py-32 bg-white dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm font-medium mb-4">
            <Briefcase className="h-4 w-4" />
            Cas d'usage
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Adapté à votre réalité
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Que vous soyez une agence, une startup ou une équipe produit, 
            Lapyno s'adapte à votre façon de travailler.
          </p>
        </div>

        {/* Use cases grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {USE_CASES.map((useCase) => {
            const Icon = useCase.icon;
            const gradient = colorMap[useCase.color as keyof typeof colorMap];
            
            return (
              <div
                key={useCase.title}
                className="group relative p-6 md:p-8 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                {/* Background gradient on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                
                <div className="relative">
                  {/* Icon */}
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${gradient} text-white mb-5`}>
                    <Icon className="h-6 w-6" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                    {useCase.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-600 dark:text-slate-400 mb-5 leading-relaxed">
                    {useCase.description}
                  </p>

                  {/* Benefits */}
                  <div className="flex flex-wrap gap-2">
                    {useCase.benefits.map((benefit) => (
                      <span
                        key={benefit}
                        className="px-3 py-1 text-sm rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
